import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { useForm, SubmitHandler } from "react-hook-form";
import { runInAction } from "mobx";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { Nameplate } from "src/api/Notification";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const NameplatesBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;

    const nameplate_schema = yup.object().shape({
        inscription: yup.string().required(t("form.required_field")),
        belldifferent: yup.boolean().required(t("form.required_field")),
        bellinscription: yup.string().when("belldifferent", {
            is: true,
            then: yup.string().required(t("form.required_field"))
        }),
        mailboxdifferent: yup.boolean().required(t("form.required_field")),
        mailboxinscription: yup.string().when("mailboxdifferent", {
            is: true,
            then: yup.string().required(t("form.required_field"))
        }),
        doordifferent: yup.boolean().required(t("form.required_field")),
        doorinscription: yup.string().when("doordifferent", {
            is: true,
            then: yup.string().required(t("form.required_field"))
        })
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch
    } = useForm<Nameplate>({
        resolver: yupResolver(nameplate_schema)
    });

    const watchBell = watch("belldifferent");
    const watchMailbox = watch("mailboxdifferent");
    const watchDoor = watch("doordifferent");

    useEffect(() => {
        document?.getElementById("inscription")?.focus();
        const { nameplate } = easyContactStore.notification;
        setValue("inscription", nameplate?.inscription || "");
        setValue("belldifferent", nameplate?.belldifferent || false);
        setValue("bellinscription", nameplate?.bellinscription || "");
        setValue("mailboxdifferent", nameplate?.mailboxdifferent || false);
        setValue("mailboxinscription", nameplate?.mailboxinscription || "");
        setValue("doordifferent", nameplate?.doordifferent || false);
        setValue("doorinscription", nameplate?.doorinscription || "");
    }, [easyContactStore.notification, setValue]);

    const onSubmit: SubmitHandler<Nameplate> = useCallback(
        (data: Nameplate) => {
            runInAction(() => {
                if (data.inscription) {
                    easyContactStore.setNameplate(data);
                }
            });

            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    const onChangeBell: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            runInAction(() => {
                if (event.target) {
                    setValue("belldifferent", event.target.checked);
                }
            });
        },
        [setValue]
    );

    const onChangeMailbox: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            runInAction(() => {
                if (event.target) {
                    setValue("mailboxdifferent", event.target.checked);
                }
            });
        },
        [setValue]
    );

    const onChangeDoor: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            runInAction(() => {
                if (event.target) {
                    setValue("doordifferent", event.target.checked);
                }
            });
        },
        [setValue]
    );

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <UI.Input
                                {...register("inscription")}
                                type="text"
                                as="textarea"
                                errorMsg={errors.inscription ? errors.inscription.message : undefined}
                            />
                            <UI.Checkbox
                                {...register("belldifferent")}
                                label={t("screens.nameplates.bell")}
                                onChange={onChangeBell}
                                className="Checkbox"
                            />
                            {watchBell ? (
                                <UI.Input
                                    {...register("bellinscription")}
                                    type="text"
                                    as="textarea"
                                    errorMsg={errors.bellinscription ? errors.bellinscription.message : undefined}
                                />
                            ) : undefined}
                            <UI.Checkbox
                                {...register("mailboxdifferent")}
                                label={t("screens.nameplates.mailbox")}
                                onChange={onChangeMailbox}
                                className="Checkbox"
                            />
                            {watchMailbox ? (
                                <UI.Input
                                    {...register("mailboxinscription")}
                                    type="text"
                                    as="textarea"
                                    errorMsg={errors.mailboxinscription ? errors.mailboxinscription.message : undefined}
                                />
                            ) : undefined}
                            <UI.Checkbox
                                {...register("doordifferent")}
                                label={t("screens.nameplates.door")}
                                onChange={onChangeDoor}
                                className="Checkbox"
                            />
                            {watchDoor ? (
                                <UI.Input
                                    {...register("doorinscription")}
                                    type="text"
                                    as="textarea"
                                    errorMsg={errors.doorinscription ? errors.doorinscription.message : undefined}
                                />
                            ) : undefined}
                            <UI.Button onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Nameplates = Sentry.withProfiler(observer(NameplatesBase));
